import React, { useEffect, useId, useMemo, useState } from "react";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import WeaveLoader from "../common/WeaveLoader";
import { FilterChip, RowChevron } from "../../pages/site/seo/shared";
import { apiTokensService } from "../../services/apiTokens";
import { HttpStatus, MethodBadge, SECTION_LABEL } from "./shared";

const TIMEFRAMES = [
  { id: "1h", hours: 1, label: "1h" },
  { id: "1d", hours: 24, label: "1d" },
  { id: "1w", hours: 24 * 7, label: "1w" },
  { id: "30d", hours: 24 * 30, label: "30d" },
  { id: "1y", hours: 24 * 365, label: "1y" },
];

const STATUS_COLORS = ["#22c55e", "#3b82f6", "#f59e0b", "#ef4444", "#a855f7", "#14b8a6"];

function useThemeColors() {
  return useMemo(() => {
    const style = getComputedStyle(document.documentElement);
    const get = (prop) => style.getPropertyValue(prop).trim() || undefined;
    return {
      text: get("--theme-text") || "#111827",
      shadow: get("--theme-shadow") || "#E5E7EB",
      primary: get("--theme-primary") || "#0D9488",
      accent: get("--theme-secondary") || "#14b8a6",
      mainBg: get("--theme-main-bg") || "#FFFFFF",
    };
  }, []);
}

const ChartTooltip = ({ active, payload, label, valueKey = "count", valueSuffix = "" }) => {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload || {};
  const title = row.label || row.endpoint || row.name || row.status_code || label || "";
  const raw = payload[0]?.value ?? row[valueKey] ?? 0;
  const suffix = valueSuffix || (valueKey === "avg_ms" ? "ms" : ` request${raw === 1 ? "" : "s"}`);
  return (
    <div className="rounded-md border border-theme-secondary bg-theme-main px-3 py-2 shadow-lg text-xs text-theme-text pointer-events-none">
      <div className="font-medium max-w-[16rem] break-all">{String(title)}</div>
      <div className="opacity-70 mt-0.5">
        {raw}
        {suffix.startsWith(" ") ? suffix : ` ${suffix}`}
      </div>
    </div>
  );
};

function formatTick(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  if (String(value).includes("T")) {
    return date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  }
  return date.toLocaleDateString([], { month: "short", day: "numeric" });
}

const ChartCard = ({ title, children }) => (
  <div className="border border-theme-secondary rounded-2xl p-4 h-56 flex flex-col min-w-0">
    <h3 className={`mb-2 shrink-0 ${SECTION_LABEL}`}>{title}</h3>
    <div className="flex-1 min-h-0">{children}</div>
  </div>
);

const EmptyChart = ({ label = "No data in this window." }) => (
  <div className="h-full flex items-center justify-center text-xs opacity-50">{label}</div>
);

const ApiUsage = ({ onSelectRequest }) => {
  const colors = useThemeColors();
  const gradId = useId().replace(/:/g, "");
  const [timeframe, setTimeframe] = useState("30d");
  const [summary, setSummary] = useState(null);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);
  const hours = TIMEFRAMES.find((t) => t.id === timeframe)?.hours || 24 * 30;

  useEffect(() => {
    setLoading(true);
    const params = { hours };
    apiTokensService.usage(
      params,
      (res) => setSummary(res.data),
      () =>
        setSummary({
          total_requests: 0,
          avg_latency_ms: 0,
          timeseries: [],
          latency: [],
          endpoints: [],
          status_codes: [],
        }),
    );
    apiTokensService.listRequests(
      { ...params, page_size: 20 },
      (res) => {
        const rows = res.data?.results || res.data || [];
        setRecent(Array.isArray(rows) ? rows : []);
        setLoading(false);
      },
      () => {
        setRecent([]);
        setLoading(false);
      },
    );
  }, [hours]);

  if (loading && !summary) {
    return (
      <div className="flex justify-center py-16">
        <WeaveLoader size={64} />
      </div>
    );
  }

  const timeseries = summary?.timeseries || [];
  const latency = summary?.latency || [];
  const endpoints = (summary?.endpoints || []).map((row) => ({
    ...row,
    label: row.label || `${row.method || "GET"} ${row.endpoint || ""}`.trim(),
  }));
  const statusCodes = (summary?.status_codes || []).map((row) => ({
    ...row,
    name: String(row.status_code),
  }));

  return (
    <div className="space-y-6 pb-8">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-baseline gap-4">
          <div>
            <span className="text-2xl font-semibold text-theme-text">{summary?.total_requests ?? 0}</span>
            <span className="ml-1.5 text-sm text-theme-secondary-text">calls</span>
          </div>
          <div>
            <span className="text-2xl font-semibold text-theme-text">{summary?.avg_latency_ms ?? 0}ms</span>
            <span className="ml-1.5 text-sm text-theme-secondary-text">avg latency</span>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          {TIMEFRAMES.map((t) => (
            <FilterChip key={t.id} active={timeframe === t.id} onClick={() => setTimeframe(t.id)}>
              {t.label}
            </FilterChip>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        <ChartCard title="Requests over time">
          {timeseries.length === 0 ? (
            <EmptyChart />
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={timeseries} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
                <defs>
                  <linearGradient id={`apiRequestsFill-${gradId}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={colors.primary} stopOpacity={0.35} />
                    <stop offset="100%" stopColor={colors.primary} stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke={colors.shadow} strokeOpacity={0.35} vertical={false} />
                <XAxis
                  dataKey="date"
                  tickFormatter={formatTick}
                  tick={{ fontSize: 10, fill: colors.text, fillOpacity: 0.5 }}
                  stroke={colors.shadow}
                  tickLine={false}
                  axisLine={false}
                  minTickGap={24}
                />
                <YAxis
                  allowDecimals={false}
                  width={28}
                  tick={{ fontSize: 10, fill: colors.text, fillOpacity: 0.5 }}
                  stroke={colors.shadow}
                  tickLine={false}
                  axisLine={false}
                />
                <Tooltip content={<ChartTooltip />} wrapperStyle={{ outline: "none", zIndex: 20 }} />
                <Area
                  type="monotone"
                  dataKey="count"
                  stroke={colors.primary}
                  strokeWidth={2}
                  fill={`url(#apiRequestsFill-${gradId})`}
                  dot={false}
                />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        <ChartCard title="By endpoint">
          {endpoints.length === 0 ? (
            <EmptyChart />
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={endpoints.slice(0, 8)}
                layout="vertical"
                margin={{ top: 4, right: 8, bottom: 4, left: 4 }}
              >
                <CartesianGrid stroke={colors.shadow} strokeOpacity={0.35} horizontal={false} />
                <XAxis type="number" allowDecimals={false} hide />
                <YAxis
                  type="category"
                  dataKey="label"
                  width={96}
                  tick={{ fontSize: 9, fill: colors.text, fillOpacity: 0.7 }}
                  stroke={colors.shadow}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(value) => (value.length > 16 ? `${value.slice(0, 14)}…` : value)}
                />
                <Tooltip content={<ChartTooltip />} wrapperStyle={{ outline: "none", zIndex: 20 }} />
                <Bar dataKey="count" fill={colors.primary} radius={[0, 4, 4, 0]} maxBarSize={22} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        <ChartCard title="Status codes">
          {statusCodes.length === 0 ? (
            <EmptyChart />
          ) : (
            <div className="flex items-center gap-3 h-full min-w-0">
              <div className="flex-1 min-w-0 h-full">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={statusCodes}
                      dataKey="count"
                      nameKey="name"
                      innerRadius="50%"
                      outerRadius="75%"
                      paddingAngle={2}
                      stroke={colors.mainBg}
                      strokeWidth={2}
                    >
                      {statusCodes.map((row, index) => (
                        <Cell
                          key={row.status_code}
                          fill={STATUS_COLORS[index % STATUS_COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <Tooltip content={<ChartTooltip />} wrapperStyle={{ outline: "none", zIndex: 20 }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="shrink-0 space-y-1.5 pr-1">
                {statusCodes.map((row, index) => (
                  <div key={row.status_code} className="flex items-center gap-1.5 text-xs">
                    <span
                      className="w-2 h-2 rounded-full"
                      style={{ backgroundColor: STATUS_COLORS[index % STATUS_COLORS.length] }}
                    />
                    <span className="font-medium">{row.status_code}</span>
                    <span className="opacity-60">{row.count}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </ChartCard>

        <ChartCard title="Latency">
          {latency.length === 0 ? (
            <EmptyChart />
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={latency} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
                <defs>
                  <linearGradient id={`apiLatencyFill-${gradId}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={colors.accent || colors.primary} stopOpacity={0.35} />
                    <stop offset="100%" stopColor={colors.accent || colors.primary} stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke={colors.shadow} strokeOpacity={0.35} vertical={false} />
                <XAxis
                  dataKey="date"
                  tickFormatter={formatTick}
                  tick={{ fontSize: 10, fill: colors.text, fillOpacity: 0.5 }}
                  stroke={colors.shadow}
                  tickLine={false}
                  axisLine={false}
                  minTickGap={24}
                />
                <YAxis
                  allowDecimals={false}
                  width={36}
                  tick={{ fontSize: 10, fill: colors.text, fillOpacity: 0.5 }}
                  stroke={colors.shadow}
                  tickLine={false}
                  axisLine={false}
                />
                <Tooltip
                  content={<ChartTooltip valueKey="avg_ms" valueSuffix="ms avg" />}
                  wrapperStyle={{ outline: "none", zIndex: 20 }}
                />
                <Area
                  type="monotone"
                  dataKey="avg_ms"
                  stroke={colors.accent || colors.primary}
                  strokeWidth={2}
                  fill={`url(#apiLatencyFill-${gradId})`}
                  dot={false}
                />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </ChartCard>
      </div>

      <section>
        <h2 className={`mb-3 ${SECTION_LABEL}`}>Recent requests</h2>
        {recent.length === 0 ? (
          <p className="py-10 text-center text-sm text-theme-secondary-text">No requests in this window.</p>
        ) : (
          <ul className="divide-y divide-theme-secondary overflow-hidden rounded-2xl border border-theme-secondary">
            {recent.map((row) => (
              <li key={row.hash}>
                <button
                  type="button"
                  onClick={() => onSelectRequest?.(row)}
                  className="flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm seo-row-hover"
                >
                  <MethodBadge method={row.method} />
                  <span className="min-w-0 flex-1 truncate font-mono text-xs">{row.endpoint}</span>
                  <HttpStatus code={row.status_code} />
                  <span className="hidden w-40 shrink-0 text-right text-xs text-theme-secondary-text sm:inline">
                    {row.create_date ? new Date(row.create_date).toLocaleString() : ""}
                  </span>
                  <RowChevron />
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
};

export default ApiUsage;
